import { FormField, Select } from '@chopchop-se/ui';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  articleLookupKey,
  fetchArticleOptions,
  fetchProductionAreaOptions,
  productionAreaLookupKey,
} from '../lib/cross-module';

/**
 * Pickers over registers this module does not own (see `lib/cross-module.ts`).
 *
 * When the register endpoint is not there yet — Wave 1 lands modules in
 * parallel — the picker degrades to an id field with a hint that says exactly
 * why, instead of rendering an empty dropdown that looks like a bug or, worse,
 * a hardcoded list that goes stale. The moment M1/M2 ship, these turn into real
 * dropdowns with no change here.
 */

export function ArticlePicker({
  value,
  onChange,
  label,
  error,
}: {
  value: string;
  onChange: (id: string) => void;
  label: string;
  error?: string | undefined;
}): ReactNode {
  const { t } = useTranslation('production');
  const [search] = useState('');
  const query = useQuery({
    queryKey: articleLookupKey(search),
    queryFn: () => fetchArticleOptions(search),
    retry: false,
    staleTime: 60_000,
  });

  if (query.isError) {
    return (
      <FormField
        label={t('pickers.articleFallbackLabel')}
        hint={<span className="text-xs text-ink-muted">{t('pickers.articleFallbackHint')}</span>}
        value={value}
        onChange={(event) => {
          onChange(event.target.value.trim());
        }}
        error={error}
        placeholder="00000000-0000-0000-0000-000000000000"
      />
    );
  }

  return (
    <Select
      label={label}
      searchable
      placeholder={query.isLoading ? t('pickers.loading') : t('orders.form.selectArticle')}
      options={(query.data ?? []).map((article) => ({
        value: article.id,
        label: `${article.articleNo} · ${article.name}`,
      }))}
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
      error={error}
    />
  );
}

export function ProductionAreaPicker({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (id: string) => void;
  label: string;
}): ReactNode {
  const { t } = useTranslation('production');
  const query = useQuery({
    queryKey: productionAreaLookupKey(),
    queryFn: fetchProductionAreaOptions,
    retry: false,
    staleTime: 300_000,
  });

  if (query.isError) {
    return (
      <FormField
        label={t('pickers.areaFallbackLabel')}
        hint={<span className="text-xs text-ink-muted">{t('pickers.areaFallbackHint')}</span>}
        value={value}
        onChange={(event) => {
          onChange(event.target.value.trim());
        }}
        placeholder="00000000-0000-0000-0000-000000000000"
      />
    );
  }

  return (
    <Select
      label={label}
      placeholder={query.isLoading ? t('pickers.loading') : t('orders.form.selectArea')}
      options={[
        { value: '', label: t('pickers.none') },
        ...(query.data ?? []).map((area) => ({ value: area.id, label: `${area.code} · ${area.name}` })),
      ]}
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}
